"use client";

/**
 * Collapsible, indexable transcript of a manifest video. Native `<details>`
 * (works without JavaScript, content stays in the server-rendered HTML for
 * search engines and screen readers). The transcript is the video's spoken
 * Dutch, rendered per scene with short headings — never a raw SRT dump —
 * inside `lang="nl"` so assistive tech switches pronunciation correctly.
 */

import { useT } from "@/i18n";
import type { MarketingVideo } from "@/lib/marketing-videos";

export function VideoTranscript({ video }: { video: MarketingVideo }) {
  const t = useT();
  if (!video.transcript || video.transcript.length === 0) return null;
  return (
    <details className="group mx-auto mt-6 max-w-3xl rounded-2xl border border-line-subtle bg-bg-raised">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-5 py-4 text-[14px] font-medium text-fg-bright">
        {t("videoKit.transcriptToggle")}
        <span aria-hidden className="text-fg-muted transition group-open:rotate-45">
          <span className="block text-xl leading-none">+</span>
        </span>
      </summary>
      <div lang="nl" className="border-t border-line-subtle px-5 pb-5">
        {video.transcript.map((section) => (
          <section key={section.title} className="mt-4">
            <h3 className="text-[13px] font-semibold text-fg-bright">{section.title}</h3>
            {section.paragraphs.map((p) => (
              <p key={p} className="mt-1.5 text-[13px] leading-relaxed text-fg-dim">
                {p}
              </p>
            ))}
          </section>
        ))}
      </div>
    </details>
  );
}
